import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { teamsApi } from '../services/api';
import { Download } from 'lucide-react';

export const ExportPage: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [teamNumber, setTeamNumber] = useState('');
  const [eventName, setEventName] = useState('');
  const [allianceColor, setAllianceColor] = useState('');

  const handleExport = async (format: 'excel' | 'csv' | 'pdf') => {
    try {
      setIsLoading(true);
      let response;
      const filename = `frc-scouting-${new Date().toISOString().split('T')[0]}`;

      // Build filters object
      const filters: any = {};
      if (eventName) filters.event_name = eventName;
      if (allianceColor) filters.alliance_color = allianceColor;
      if (teamNumber) filters.team_numbers = [parseInt(teamNumber)];

      if (format === 'excel') {
        response = await teamsApi.exportExcel(filters);
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('href', url);
        link.setAttribute('download', `${filename}.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.parentNode?.removeChild(link);
      } else if (format === 'csv') {
        response = await teamsApi.exportCSV(filters);
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${filename}.csv`);
        document.body.appendChild(link);
        link.click();
        link.parentNode?.removeChild(link);
      } else if (format === 'pdf') {
        response = await teamsApi.exportPDF(
          teamNumber ? parseInt(teamNumber) : undefined,
          eventName || undefined,
          allianceColor || undefined
        );
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${filename}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.parentNode?.removeChild(link);
      }

      alert(`✓ ${format.toUpperCase()} exported successfully!`);
    } catch (err) {
      alert(`✗ Error exporting ${format.toUpperCase()}`);
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const exportOptions = [
    {
      format: 'excel' as const,
      title: 'Excel Spreadsheet',
      description: 'Download all scouting data as .xlsx file',
      color: 'from-green-500 to-green-600',
    },
    {
      format: 'csv' as const,
      title: 'CSV Data',
      description: 'Comma-separated values for spreadsheets',
      color: 'from-blue-500 to-blue-600',
    },
    {
      format: 'pdf' as const,
      title: 'PDF Report',
      description: 'Formatted report summary',
      color: 'from-red-500 to-red-600',
    },
  ];

  return (
    <div>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
        <div className="glass p-6">
          <h1 className="section-title flex items-center gap-2">
            <Download className="w-6 h-6" />
            Export Data
          </h1>
          <p className="text-gray-400">Download scouting data in various formats</p>
        </div>
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="glass p-6 mb-6">
        <h2 className="subsection-title mb-4">Filter Options (Leave blank for all teams)</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Team Number</label>
            <input
              type="number"
              value={teamNumber}
              onChange={(e) => setTeamNumber(e.target.value)}
              placeholder="Leave blank for all teams"
              className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Event Name</label>
            <input
              type="text"
              value={eventName}
              onChange={(e) => setEventName(e.target.value)}
              placeholder="Leave blank for all events"
              className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Alliance Color</label>
            <select
              value={allianceColor}
              onChange={(e) => setAllianceColor(e.target.value)}
              className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-blue-500"
            >
              <option value="">All Colors</option>
              <option value="Red">Red Alliance</option>
              <option value="Blue">Blue Alliance</option>
            </select>
          </div>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {exportOptions.map((option, idx) => (
          <motion.div
            key={option.format}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.1 }}
            className="glass p-6"
          >
            <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${option.color} flex items-center justify-center mb-4`}>
              <Download className="w-6 h-6 text-white" />
            </div>
            <h3 className="font-semibold mb-2">{option.title}</h3>
            <p className="text-sm text-gray-400 mb-4">{option.description}</p>
            <motion.button
              onClick={() => handleExport(option.format)}
              disabled={isLoading}
              whileTap={{ scale: 0.98 }}
              className="w-full btn btn-primary"
            >
              {isLoading ? 'Exporting...' : `Export ${option.format.toUpperCase()}`}
            </motion.button>
          </motion.div>
        ))}
      </div>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 20 }} transition={{ delay: 0.4 }} className="glass p-6 mt-6">
        <h2 className="subsection-title">Export Features</h2>
        <ul className="space-y-2 text-gray-300 text-sm">
          <li>✓ All scouting submissions included</li>
          <li>✓ Team statistics and rankings</li>
          <li>✓ Performance metrics and trends</li>
          <li>✓ Date, time, and scout information</li>
          <li>✓ Ready for analysis in Excel or other tools</li>
          <li>✓ Printable PDF reports</li>
        </ul>
      </motion.div>
    </div>
  );
};
